import { useEffect, type RefObject } from 'react';
import { startEditorFocusRetry } from '../utils/editorFocusRetry';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { consumeReferenceReveal } from '../../../navigational-features/find-references/openReferenceLocation';
import type { QueryEditorHandle } from '../components/QueryEditor';

type UseEditorMountActionsParams = {
  tabId: string;
  activeTabId: string | null;
  /** Snippet template queued by "Script New..." context-menu actions. */
  insertSnippetOnMount: string | undefined;
  /** The tab's persisted query text (re-arms the reveal retry when it lands). */
  storeQuery: string | undefined;
  isAiChatOpen: boolean;
  /** The Visual view covers the editor — it takes focus itself. */
  editorHidden?: boolean;
  editorRef: RefObject<QueryEditorHandle | null>;
};

/**
 * Mount-time editor actions for a query editor tab: bounded-retry snippet
 * insertion ("Script New..."), the queued find-references reveal, and
 * focus-on-activation. All three retry until the Monaco instance is ready
 * rather than firing a single delayed shot. Behavior carried verbatim from
 * the previous inline implementation.
 */
export function useEditorMountActions({
  tabId,
  activeTabId,
  insertSnippetOnMount,
  storeQuery,
  isAiChatOpen,
  editorHidden = false,
  editorRef,
}: UseEditorMountActionsParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  // Handle snippet insertion on mount (for "Script New..." context menu actions).
  // The editor ref may not be ready on the first tick (a freshly-added tab can
  // mount the Monaco instance slightly later), so retry on a bounded interval
  // until insertSnippet succeeds rather than a single 100ms shot — otherwise the
  // template is silently dropped when the editor mounts late.
  useEffect(() => {
    if (!insertSnippetOnMount) return;

    let attempts = 0;
    const maxAttempts = 50; // ~5s at 100ms intervals
    const intervalId = setInterval(() => {
      attempts += 1;
      if (editorRef.current?.insertSnippet(insertSnippetOnMount)) {
        // Clear the flag so it doesn't re-trigger
        updateTabContent(tabId, { insertSnippetOnMount: undefined });
        clearInterval(intervalId);
      } else if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [insertSnippetOnMount, tabId, updateTabContent, editorRef]);

  // Honor a queued reveal-at-position request from the find-references tab
  // (NavigationalFeatures Slice 24). The queue is a global scratchpad keyed
  // by tabId; consuming clears the entry so a subsequent navigation lands
  // a fresh request.
  useEffect(() => {
    let cancelled = false;
    const tryReveal = (deadline: number) => {
      if (cancelled) return;
      const target = consumeReferenceReveal(tabId);
      if (!target) return;
      if (!editorRef.current?.revealLine) {
        if (Date.now() > deadline) return;
        setTimeout(() => tryReveal(deadline), 50);
        return;
      }
      // If the buffer has not been populated yet (still streaming the CREATE
      // script), wait briefly for the synchronous setValue path before
      // revealing — otherwise the editor reveals an out-of-bounds line.
      const value = editorRef.current.getValue?.() ?? '';
      if (!value && Date.now() <= deadline) {
        setTimeout(() => tryReveal(deadline), 50);
        return;
      }
      editorRef.current.revealLine(target.line, target.column);
    };
    tryReveal(Date.now() + 5000);
    return () => {
      cancelled = true;
    };
  }, [tabId, storeQuery, editorRef]);

  // Focus editor when this tab becomes active (skip when AI chat is open so the textarea keeps focus)
  useEffect(() => {
    if (activeTabId !== tabId) return;
    if (isAiChatOpen || editorHidden) return;

    // Try to focus immediately, then retry if not ready
    const tryFocus = () => {
      const activeElement = document.activeElement;
      const activeTestId =
        activeElement instanceof HTMLElement
          ? activeElement.getAttribute('data-testid')
          : null;
      if (activeTestId?.startsWith('tab-edit-input-')) {
        return true;
      }
      return editorRef.current?.focus() ?? false;
    };

    if (tryFocus()) return;

    // Retry until the editor is ready — but never take focus back from a
    // control the user reached after this tab was activated. An unguarded
    // tick landing on an open popover closed it ~2 ms after it opened, which
    // is what "the first click on Copy did nothing" was (#123). See
    // `editorFocusRetry.ts` for the measured chain.
    return startEditorFocusRetry({ focusEditor: tryFocus, doc: document });
  }, [activeTabId, tabId, isAiChatOpen, editorHidden, editorRef]);
}
